import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Meal, Venue } from '../../../types';
import type { PlaceBasicDetails } from '../../../api/places.api';
import MealPlacePicker from './MealPlacePicker';
import MealIngredients, { type IngredientActions } from './MealIngredients';
import MealStopLink, { type MealStopActions } from './MealStopLink';
import { asTyped } from './pantryUnits';

interface Props {
  meal: Meal;
  venues: Venue[];
  /** Resolves true once saved, for the "Saved" flash. */
  onChange: (patch: { notes?: string | null; venueId?: string | null }) => Promise<boolean>;
  /** Saves a searched place in Places (or reuses it) and links the meal to it. */
  onPickPlace: (place: PlaceBasicDetails) => Promise<void>;
  ingredients: IngredientActions;
  stops: MealStopActions;
  onClose: () => void;
}

/** The optional parts of a meal: a free note, the place it happens at, its stop in the plan, and what it uses. */
export default function MealDetails({ meal, venues, onChange, onPickPlace, ingredients, stops, onClose }: Props) {
  const { t } = useTranslation();
  // Everything here saves on its own (on blur, on pick); the flash is what
  // tells the user it did, since there is no Save button to have pressed.
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return;
    const id = setTimeout(() => setSaved(false), 1800);
    return () => clearTimeout(id);
  }, [saved]);

  async function change(patch: { notes?: string | null; venueId?: string | null }) {
    if (await onChange(patch)) setSaved(true);
  }
  const flashed = <A extends unknown[]>(fn: (...args: A) => Promise<boolean>) =>
    async (...args: A) => {
      const ok = await fn(...args);
      if (ok) setSaved(true);
      return ok;
    };

  return (
    <div className="mt-1.5 mb-1 pl-1 space-y-1.5">
      <input
        defaultValue={meal.notes ?? ''}
        onBlur={e => {
          const notes = e.target.value.trim() || null;
          if (notes !== meal.notes) void change({ notes });
        }}
        placeholder={t('meals.notesPlaceholder')}
        className="input w-full !text-xs !px-2.5 !py-1.5"
      />
      <MealPlacePicker
        venues={venues}
        venueId={meal.venueId}
        onSelectVenue={venueId => void change({ venueId })}
        onPickPlace={onPickPlace}
        emptyLabel={t('meals.noVenue')}
      />
      <p className="text-[11px] text-slate-400 dark:text-slate-500">
        {t('meals.venueHint')}
      </p>
      <div className="pt-1">
        <MealStopLink meal={meal} stops={stops} />
      </div>
      {/* A meal at a restaurant spends nothing from the pantry. */}
      {!meal.venueId && (
        <div className="pt-1.5">
          <MealIngredients
            items={meal.ingredients.map(i => ({
              key: i.id, name: i.pantryItem.name, label: i.label, typed: asTyped(i.amount, i.pantryItem.unit),
            }))}
            products={ingredients.products}
            onAdd={flashed((name, amount, unit) => ingredients.onAdd(meal.id, name, amount, unit))}
            onEdit={flashed((id, amount, unit) => ingredients.onEdit(meal.id, id, amount, unit))}
            onRemove={id => ingredients.onRemove(meal.id, id)}
          />
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <span
          aria-live="polite"
          className={`text-[11px] text-teal-600 dark:text-teal-400 transition-opacity ${saved ? 'opacity-100' : 'opacity-0'}`}
        >
          {saved ? t('meals.savedFlash') : ''}
        </span>
        <button
          onClick={onClose}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:opacity-90 cursor-pointer"
        >
          {t('common.done')}
        </button>
      </div>
    </div>
  );
}
